// FlavorCard.js — one priced flavor card.
// Presentational / prop-driven (like ServiceRow & DeeperTweaksModal): all state
// & math arrive precomputed on `vm` (built by the workspace, Task 10); clicks
// call callbacks. Composes ServiceRow for its per-service list.
import React from 'react';
import ServiceRow from './ServiceRow';

// Delta badge color by direction. down = cheaper (green), up = pricier
// (orange/warn), base/undefined = muted.
function deltaColor(dir) {
  if (dir === 'down') return 'var(--sf-green)';
  if (dir === 'up') return 'var(--sf-warn)';
  return 'var(--sf-muted)';
}

// One side-by-side stat box: uppercase caption + value (mono).
function StatBox({ caption, value }) {
  return (
    <div
      className="sf-card-stat"
      style={{
        flex: 1, minWidth: 0, background: 'var(--sf-inset)',
        border: '1px solid var(--sf-line)', borderRadius: 10, padding: '8px 10px',
      }}
    >
      <div
        className="sf-card-stat-cap"
        style={{
          fontSize: 10.5, letterSpacing: '.04em', textTransform: 'uppercase',
          color: 'var(--sf-muted)', marginBottom: 4,
        }}
      >
        {caption}
      </div>
      <div
        className="sf-card-stat-val num"
        style={{ fontSize: 15, fontWeight: 600, color: 'var(--sf-ink-2)' }}
      >
        {value}
      </div>
    </div>
  );
}

export default function FlavorCard({
  vm, onOpenModal, onCycleMode, onStepFreq, onStepDur,
  onSetPerVisitCost, onSetTrees, onSetPalms, onSetRecommended,
}) {
  return (
    <div className={`sf-card${vm.recommended ? ' is-recommended' : ''}`}>
      {/* Header: flavor name + RECOMMENDED badge */}
      <div
        className="sf-card-head"
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}
      >
        <span
          className="sf-card-name"
          style={{
            fontWeight: 800, fontSize: 14, letterSpacing: '.04em',
            textTransform: 'uppercase', color: 'var(--sf-ink)',
          }}
        >
          {vm.name}
        </span>
        {vm.recommended ? (
          <button
            type="button"
            className="sf-card-badge"
            aria-pressed="true"
            title="Click to clear the recommendation"
            onClick={() => onSetRecommended && onSetRecommended(vm.id)}
            style={{
              marginLeft: 'auto', background: '#1d4d33', color: '#fff',
              border: 'none', cursor: 'pointer',
              borderRadius: 999, padding: '3px 9px', fontSize: 10,
              fontWeight: 800, letterSpacing: '.06em',
            }}
          >
            RECOMMENDED
          </button>
        ) : (
          <button
            type="button"
            className="sf-card-badge sf-card-badge--ghost"
            aria-pressed="false"
            title="Mark this tier as the recommended plan"
            onClick={() => onSetRecommended && onSetRecommended(vm.id)}
            style={{
              marginLeft: 'auto', background: 'transparent', color: 'var(--sf-muted)',
              border: '1px dashed var(--sf-line-2)', cursor: 'pointer',
              borderRadius: 999, padding: '3px 9px', fontSize: 10,
              fontWeight: 700, letterSpacing: '.06em',
            }}
          >
            Recommend
          </button>
        )}
      </div>

      {/* Annual price */}
      <div
        className="sf-card-price num"
        style={{ fontSize: 28, fontWeight: 700, color: 'var(--sf-ink)', lineHeight: 1.1 }}
      >
        {vm.priceStr}
      </div>

      {/* Monthly + delta badge */}
      <div
        className="sf-card-sub"
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginBottom: 12 }}
      >
        <span
          className="sf-card-monthly num"
          style={{ fontSize: 13, color: 'var(--sf-muted)' }}
        >
          {vm.monthlyStr}
        </span>
        <span
          className="sf-card-delta"
          style={{
            marginLeft: 'auto', fontSize: 11.5, fontWeight: 700,
            color: deltaColor(vm.deltaDir),
          }}
        >
          {vm.deltaStr}
        </span>
      </div>

      {/* Two stat boxes: $/SF·yr and Cost/yr */}
      <div
        className="sf-card-stats"
        style={{ display: 'flex', gap: 8, marginBottom: 10 }}
      >
        <StatBox caption="$/SF·yr" value={vm.sfStr} />
        <StatBox caption="Cost" value={`${vm.costStr}/yr`} />
      </div>

      {/* Drive-time / trips line removed this pass (drive-time deferred). */}

      {/* Tax line — only when taxable */}
      {vm.taxable ? (
        <div
          className="sf-card-tax"
          style={{ fontSize: 11.5, color: 'var(--sf-ink-3)', marginBottom: 8 }}
        >
          Incl. <span className="num">{vm.taxRateStr}</span> tax →{' '}
          <span className="num">{vm.withTaxStr}</span>
        </div>
      ) : null}

      {/* Start-up line — only when hasStartup */}
      {vm.hasStartup ? (
        <div
          className="sf-card-startup"
          style={{ fontSize: 11.5, color: 'var(--sf-ink-3)', marginBottom: vm.startupItems?.length ? 4 : 12 }}
        >
          One-time start-up → <span className="num">{vm.startupStr}</span>
        </div>
      ) : null}
      {vm.hasStartup && vm.startupItems?.length ? (
        <div
          className="sf-card-startup-items"
          style={{ fontSize: 10.5, color: 'var(--sf-muted)', marginBottom: 12, lineHeight: 1.5 }}
        >
          {vm.startupItems.join(' · ')}
        </div>
      ) : null}

      {/* Deeper tweaks button */}
      <button
        type="button"
        className="sf-card-tweaks"
        onClick={onOpenModal}
        style={{
          width: '100%', textAlign: 'left', cursor: 'pointer',
          border: '1px solid var(--sf-line-2)', borderRadius: 10,
          background: 'var(--sf-surface)', padding: '10px 12px',
          marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 2,
        }}
      >
        <span
          className="sf-card-tweaks-label"
          style={{ fontSize: 13, fontWeight: 700, color: 'var(--sf-ink-2)' }}
        >
          ⚙ Deeper tweaks
        </span>
        <span
          className="sf-card-tweaks-sub"
          style={{ fontSize: 11, color: 'var(--sf-muted)' }}
        >
          schedule · trim · repairs · start-up
        </span>
      </button>

      {/* Service rows */}
      <div className="sf-card-services">
        {(vm.services || []).map((s) => (
          <ServiceRow
            key={s.meta.role}
            meta={s.meta}
            st={s.st}
            totalStr={s.totalStr}
            note={s.note}
            freqLabel={s.freqLabel}
            durLabel={s.durLabel}
            durCaption={s.durCaption}
            freqCaption={s.freqCaption}
            feeStr={s.feeStr}
            onCycleMode={() => onCycleMode(vm.id, s.meta.role)}
            onStepFreq={(d) => onStepFreq(vm.id, s.meta.role, d)}
            onStepDur={(d) => onStepDur(vm.id, s.meta.role, d)}
            onSetPerVisitCost={(value) => onSetPerVisitCost(vm.id, s.meta.role, value)}
            onSetTrees={onSetTrees ? (value) => onSetTrees(vm.id, value) : undefined}
            onSetPalms={onSetPalms ? (value) => onSetPalms(vm.id, value) : undefined}
          />
        ))}
      </div>

      {/* Optional add-ons summary — only when hasExtras */}
      {vm.hasExtras ? (
        <div className="sf-card-extras" style={{ marginTop: 'auto' }}>
          <div
            className="sf-card-extras-divider"
            style={{ borderTop: '1px solid var(--sf-line)', margin: '4px 0 10px' }}
          />
          <div
            className="sf-card-extras-head"
            style={{
              fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase',
              color: 'var(--sf-muted)', fontWeight: 700, marginBottom: 8,
            }}
          >
            Optional add-ons · not in monthly
          </div>
          {(vm.extras || []).map((ex) => (
            <div
              key={ex.label}
              className="sf-card-extra"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5,
                color: 'var(--sf-ink-3)', marginBottom: 6 }}
            >
              <span
                className="sf-card-extra-dot"
                style={{ width: 9, height: 9, borderRadius: '50%', flex: '0 0 auto',
                  background: ex.accent || 'var(--sf-green-2)' }}
              />
              <span className="sf-card-extra-label">{ex.label}</span>
              <span className="sf-card-extra-fee num" style={{ marginLeft: 'auto' }}>{ex.feeStr}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
